import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { getCompanies } from "../../features/companies/companiesSlice";
import DashboardCard from "./components/DashboardCard";
import CompanyFilter from "../../components/common/CompanyFilter";
import {
  FaRobot,
  FaWhatsapp,
  FaUserPlus,
  FaBuilding,
  FaEnvelope,
  FaSms,
  FaHashtag,
  FaComments,
  FaChartLine,
  FaInfoCircle,
} from "react-icons/fa";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { isAdmin } from "../../utils/permissions";
import aiChatbotsService from "../../features/ai_chatbots/aiChatbotsService";

const MarketingDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { selectedAgencyId, selectedClientId } = useSelector(
    (state: RootState) => state.settings
  );
  const { companies } = useSelector((state: RootState) => state.companies);
  const { t } = useTranslation();

  const [selectedAgency, setSelectedAgency] = useState<number | undefined>(
    selectedAgencyId
  );
  const [selectedClient, setSelectedClient] = useState<number | undefined>(
    selectedClientId
  );
  const [chatbotCount, setChatbotCount] = useState<number>(0);

  // Dummy data for other (mock) tools
  const dashboardData = {
    whatsappChatbots: 8,
    leadGeneration: 145,
    companyResearch: 24,
    emailMarketing: 32,
    whatsappMarketing: 17,
    socialMedia: 56,
    communicationsHub: 93,
  };

  useEffect(() => {
    dispatch(getCompanies({}));
  }, [dispatch]);

  // Sync local state from Redux when settings change (e.g. page refresh / return from another page)
  useEffect(() => {
    setSelectedAgency(selectedAgencyId);
    setSelectedClient(selectedClientId);
  }, [selectedAgencyId, selectedClientId]);

  // Agency Owner: auto-select single agency when none is selected
  const agencies = companies.filter((c) => c.company_type === "agency");
  const isAgencyOwnerRole = user?.roles?.includes("agency_owner");
  useEffect(() => {
    if (
      isAgencyOwnerRole &&
      agencies.length === 1 &&
      selectedAgencyId === undefined
    ) {
      const agencyId = agencies[0].id;
      setSelectedAgency(agencyId);
      dispatch({ type: "settings/setSelectedAgency", payload: agencyId });
    }
  }, [isAgencyOwnerRole, agencies.length, selectedAgencyId, dispatch]);

  // Fetch chatbot count for selected client
  useEffect(() => {
    if (!selectedClient) {
      setChatbotCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await aiChatbotsService.getChatbots({
          company_id: selectedClient,
          skip: 0,
          limit: 1,
        });
        const total =
          res?.pagination?.total_count ?? res?.total ?? 0;
        if (!cancelled) setChatbotCount(typeof total === "number" ? total : 0);
      } catch {
        if (!cancelled) setChatbotCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  const handleAgencyChange = (agencyId: number | undefined) => {
    setSelectedAgency(agencyId);
    if (agencyId) {
      dispatch({ type: "settings/setSelectedAgency", payload: agencyId });
    } else {
      dispatch({ type: "settings/setSelectedAgency", payload: undefined });
    }
    setSelectedClient(undefined);
    dispatch({ type: "settings/setSelectedClient", payload: undefined });
  };

  const handleClientChange = (clientId: number | undefined) => {
    setSelectedClient(clientId);
    dispatch({ type: "settings/setSelectedClient", payload: clientId });
  };

  // Client seçili mi kontrol et
  const isClientSelected = !!selectedClient;
  const showMockupTools = isAdmin(user);

  return (
    <Container fluid className="marketing-dashboard">
      {/* Modern Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <Icon icon={FaChartLine} />
            </div>
            <div className="header-text">
              <h1 className="page-title">{t("marketing_dashboard")}</h1>
              <p className="page-subtitle">
                {t("welcome_back")}, {user?.full_name || t("user")}!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="filter-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon filter-icon">
              <i className="bi bi-funnel"></i>
            </div>
            <h5 className="section-title">{t("filters")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
          <CompanyFilter
            selectedAgency={selectedAgency}
            selectedClient={selectedClient}
            onAgencyChange={handleAgencyChange}
            onClientChange={handleClientChange}
          />
          {!isClientSelected && (
            <div className="text-muted d-flex align-items-center gap-2">
              <Icon icon={FaInfoCircle} size={16} />
              {t("please_select_a_client_company_to_access_marketing_tools")}
            </div>
          )}
        </Card.Body>
      </Card>

      {isClientSelected && (
        <>
      {/* Marketing Tools Card */}
      <Card className="content-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon tools-icon">
              <i className="bi bi-tools"></i>
            </div>
            <h5 className="section-title">{t("marketing_tools")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
          <Row xs={1} sm={2} md={3} lg={4} className="g-4">
            <Col>
              <DashboardCard
                title={t("ai_chatbots")}
                icon={<Icon icon={FaRobot} size={24} />}
                count={chatbotCount}
                color="#4e73df"
                path="/ai-chatbots"
                isEnabled={true}
              />
            </Col>

            {showMockupTools && (
              <>
            <Col>
              <DashboardCard
                title={t("whatsapp_chatbots")}
                icon={<Icon icon={FaWhatsapp} size={24} />}
                count={dashboardData.whatsappChatbots}
                color="#1cc88a"
                path="/marketing-dashboard/whatsapp-chatbots"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>
            <Col>
              <DashboardCard
                title={t("lead_generation")}
                icon={<Icon icon={FaUserPlus} size={24} />}
                count={dashboardData.leadGeneration}
                color="#f6c23e"
                path="/marketing-dashboard/lead-generation"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>

            <Col>
              <DashboardCard
                title={t("company_research")}
                icon={<Icon icon={FaBuilding} size={24} />}
                count={dashboardData.companyResearch}
                color="#e74a3b"
                path="/marketing-dashboard/company-research"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>

            <Col>
              <DashboardCard
                title={t("email_marketing")}
                icon={<Icon icon={FaEnvelope} size={24} />}
                count={dashboardData.emailMarketing}
                color="#36b9cc"
                path="/marketing-dashboard/email-marketing"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>

            <Col>
              <DashboardCard
                title={t("whatsapp_marketing")}
                icon={<Icon icon={FaSms} size={24} />}
                count={dashboardData.whatsappMarketing}
                color="#6f42c1"
                path="/marketing-dashboard/whatsapp-marketing"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>

            <Col>
              <DashboardCard
                title={t("social_media")}
                icon={<Icon icon={FaHashtag} size={24} />}
                count={dashboardData.socialMedia}
                color="#fd7e14"
                path="/marketing-dashboard/social-media-management"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>

            <Col>
              <DashboardCard
                title={t("communications_hub")}
                icon={<Icon icon={FaComments} size={24} />}
                count={dashboardData.communicationsHub}
                color="#20c997"
                path="/marketing-dashboard/communications-hub"
                isEnabled={false}
                comingSoon={true}
              />
            </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Statistics Card */}
      <Card className="content-card">
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon stats-icon">
              <i className="bi bi-graph-up"></i>
            </div>
            <h5 className="section-title">{t("marketing_statistics")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="card-body-custom">
          <div className="coming-soon">
            <div className="coming-soon-icon">
              <Icon icon={FaChartLine} size="3rem" />
            </div>
            <h6 className="coming-soon-title">{t("statistics_coming_soon")}</h6>
            <p className="coming-soon-text">
              {t("this_section_will_display_marketing_statistics")}
            </p>
          </div>
        </Card.Body>
      </Card>
        </>
      )}
    </Container>
  );
};

export default MarketingDashboard;
